import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Award,
  TrendingUp,
  Share2,
  Mail,
  ChevronLeft,
  BarChart3,
  Loader2,
  Plus,
  Trash2,
  Link2,
  Users,
  Hash,
  ExternalLink,
  UserCheck,
  AlertCircle,
  Crown,
  Lock,
  Zap,
  Trophy,
  Video,
  Target,
  Star,
  Sparkles,
  Play,
  MapPin,
  Calendar,
  Shirt,
  Camera,
  Upload,
  X,
  Pencil,
  Save,
  Scissors,
  Eye,
  Heart,
  Clock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sport-based accent colors for athlete profiles
const getSportStyle = (sport) => {
  const s = (sport || '').toLowerCase();
  if (s.includes('basketball')) return { accent: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-400', glow: 'shadow-orange-500/30' };
  if (s.includes('soccer') || s.includes('futbol')) return { accent: 'bg-green-500', border: 'border-green-500', text: 'text-green-400', glow: 'shadow-green-500/30' };
  if (s.includes('volleyball')) return { accent: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-400', glow: 'shadow-blue-500/30' };
  if (s.includes('football')) return { accent: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-400', glow: 'shadow-purple-500/30' };
  if (s.includes('lacrosse')) return { accent: 'bg-yellow-500', border: 'border-yellow-500', text: 'text-yellow-400', glow: 'shadow-yellow-500/30' };
  return { accent: 'bg-[#00d4ff]', border: 'border-[#00d4ff]', text: 'text-[#00d4ff]', glow: 'shadow-cyan-500/30' };
};

const AthleteProfile = () => {
  const { id } = useParams();
  const { token, user, isAuthenticated, hasRequiredTier } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [showConnectionDialog, setShowConnectionDialog] = useState(false);
  const [connectionForm, setConnectionForm] = useState({ game_id: '', team_side: '', jersey_number: '', notes: '' });
  const [gameSearch, setGameSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [sportsvisioStatus, setSportsvisioStatus] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Edit profile state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Clips state
  const [athleteClips, setAthleteClips] = useState([]);
  const [loadingClips, setLoadingClips] = useState(false);

  const isOwner = athlete?.claimed_by === user?.id || athlete?.user_id === user?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canEdit = isOwner || isAdmin;
  const canClaim = athlete?.claimable && !athlete?.claimed_by && isAuthenticated();
  const canViewContact = athlete?.can_view_contact || false;
  
  // Elite access check - owner can always see their own stats
  const hasEliteAccess = isAuthenticated() && hasRequiredTier('elite');
  const canViewAdvancedStats = hasEliteAccess || isOwner;

  const fetchAthlete = useCallback(async () => {
    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_URL}/api/athletes/${id}`, { headers });
      if (res.ok) setAthlete(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [id, token]);

  const fetchTeamsAndGames = useCallback(async () => {
    try {
      const [teamsRes, gamesRes] = await Promise.all([
        fetch(`${API_URL}/api/teams`),
        fetch(`${API_URL}/api/engine/games`),
      ]);
      if (teamsRes.ok) setTeams(await teamsRes.json());
      if (gamesRes.ok) setGames(await gamesRes.json());
    } catch (err) { console.error(err); }
  }, []);

  const fetchSportsVisioData = useCallback(async () => {
    if (!id) return;
    setLoadingStats(true);
    try {
      const statusRes = await fetch(`${API_URL}/api/athletes/${id}/sportsvisio/status`);
      if (statusRes.ok) {
        const status = await statusRes.json();
        setSportsvisioStatus(status);
        
        // If linked and has synced stats, fetch the cached stats
        if (status.linked && status.has_synced_stats) {
          const cacheRes = await fetch(`${API_URL}/api/sportsvisio/cache/stats/${id}`);
          if (cacheRes.ok) {
            const cacheData = await cacheRes.json();
            if (cacheData.cached) {
              setAdvancedStats(cacheData.data);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch SportsVisio data:', err);
    }
    setLoadingStats(false);
  }, [id]);

  useEffect(() => { fetchAthlete(); fetchTeamsAndGames(); }, [fetchAthlete, fetchTeamsAndGames]);
  
  useEffect(() => { 
    if (athlete && canViewAdvancedStats) {
      fetchSportsVisioData(); 
    }
  }, [athlete, canViewAdvancedStats, fetchSportsVisioData]);

  const handleAddConnection = async (e) => {
    e.preventDefault();
    if (!connectionForm.game_id || !connectionForm.team_side || !connectionForm.jersey_number) {
      toast.error('Game, team, and jersey number are required');
      return;
    }
    setSaving(true);
    try {
      // Find the selected game to get team details
      const selectedGame = games.find(g => g.game_id === connectionForm.game_id);
      const teamName = connectionForm.team_side === 'home' 
        ? selectedGame?.home_team_name 
        : selectedGame?.away_team_name;
      const teamId = connectionForm.team_side === 'home'
        ? (selectedGame?.home_team_id || connectionForm.game_id + '_home')
        : (selectedGame?.away_team_id || connectionForm.game_id + '_away');

      const res = await fetch(`${API_URL}/api/athletes/${id}/roster-connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          team_id: teamId,
          team_name: teamName,
          jersey_number: parseInt(connectionForm.jersey_number),
          game_id: connectionForm.game_id,
          game_name: `${selectedGame?.home_team_name} vs ${selectedGame?.away_team_name}`,
          notes: connectionForm.notes,
        }),
      });
      if (res.ok) {
        toast.success('Roster connection added!');
        setShowConnectionDialog(false);
        setConnectionForm({ game_id: '', team_side: '', jersey_number: '', notes: '' });
        setGameSearch('');
        fetchAthlete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add connection');
      }
    } catch { toast.error('Error adding connection'); }
    setSaving(false);
  };

  const handleRemoveConnection = async (connectionId) => {
    if (!window.confirm('Remove this roster connection?')) return;
    try {
      const res = await fetch(`${API_URL}/api/athletes/${id}/roster-connections/${connectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) { toast.success('Connection removed'); fetchAthlete(); }
      else toast.error('Failed to remove connection');
    } catch { toast.error('Error removing connection'); }
  };

  const handleClaimProfile = async () => {
    if (!isAuthenticated()) {
      toast.error('Please sign in to claim this profile');
      return;
    }
    setClaiming(true);
    try {
      const res = await fetch(`${API_URL}/api/athletes/${id}/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success(`You've claimed ${athlete.name}'s profile!`);
        fetchAthlete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to claim profile');
      }
    } catch {
      toast.error('Error claiming profile');
    }
    setClaiming(false);
  };

  // Photo upload handler
  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/uploads/athlete-photo/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Profile photo updated!');
        setShowPhotoUpload(false);
        // Refresh athlete data to get new avatar
        fetchAthlete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading photo');
    }
    setUploadingPhoto(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Fetch clips tagged with this athlete
  const fetchAthleteClips = useCallback(async () => {
    if (!id) return;
    setLoadingClips(true);
    try {
      const res = await fetch(`${API_URL}/api/athletes/${id}/clips`);
      if (res.ok) {
        setAthleteClips(await res.json());
      }
    } catch (err) {
      console.error('Error fetching athlete clips:', err);
    }
    setLoadingClips(false);
  }, [id]);

  // Open edit dialog with current athlete data
  const openEditDialog = () => {
    setEditForm({
      bio: athlete?.bio || '',
      position: athlete?.position || '',
      height: athlete?.height || '',
      date_of_birth: athlete?.date_of_birth || '',
      grade: athlete?.grade || '',
      school: athlete?.school || '',
      number: athlete?.number || '',
      team: athlete?.team || '',
      graduation_year: athlete?.graduation_year || '',
      gpa: athlete?.gpa || '',
      contact_email: athlete?.contact_email || '',
      social_links: athlete?.social_links || {},
    });
    setShowEditDialog(true);
  };

  // Save profile edits
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { ...editForm };
      if (payload.number) payload.number = parseInt(payload.number) || 0;
      
      const res = await fetch(`${API_URL}/api/athletes/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Profile updated!');
        setShowEditDialog(false);
        fetchAthlete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update profile');
      }
    } catch {
      toast.error('Error updating profile');
    }
    setSavingProfile(false);
  };

  useEffect(() => { fetchAthleteClips(); }, [fetchAthleteClips]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#00d4ff] animate-spin" />
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <Users className="h-10 w-10 text-zinc-600" />
          </div>
          <h2 
            className="text-2xl font-black text-white uppercase tracking-tight mb-3"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Athlete Not Found
          </h2>
          <p className="text-zinc-500 text-sm mb-6">This profile may have been removed or doesn't exist.</p>
          <Button className="bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold uppercase tracking-wide" asChild>
            <Link to="/athletes">Browse Athletes</Link>
          </Button>
        </div>
      </div>
    );
  }

  const connections = athlete.roster_connections || [];
  const sportStyle = getSportStyle(athlete.sport);
  const hasStats = athlete.stats && Object.keys(athlete.stats).length > 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="athlete-profile-page">
      {/* Cinematic Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Image - Basketball Court */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url('https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=2000&q=80')` 
            }}
          />
          {/* Dark Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 via-transparent to-[#0A0A0A]/60" />
        </div>
        
        {/* Back Button */}
        <div className="relative z-20 pt-4 pl-4 md:pl-8">
          <Link 
            to="/athletes" 
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-full text-zinc-300 hover:text-white hover:bg-black/80 transition-all text-xs font-medium"
            data-testid="back-to-athletes"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Athletes
          </Link>
        </div>

        {/* Hero Content */}
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-6 md:pt-10 pb-8 md:pb-14">
          {/* Mobile: centered column / Desktop: side-by-side */}
          <div className="flex flex-col items-center md:flex-row md:items-end gap-6 md:gap-10 w-full">
            
            {/* Avatar */}
            <div className="relative shrink-0 group">
              <div className={`absolute -inset-1.5 rounded-full ${sportStyle.accent} opacity-30 blur-md`} />
              <img
                src={
                  athlete.avatar
                    ? (athlete.avatar.startsWith('http') ? athlete.avatar : `${API_URL}${athlete.avatar}`)
                    : `https://api.dicebear.com/7.x/avataaars/svg?seed=${athlete.id}`
                }
                alt={athlete.name}
                className={`relative w-28 h-28 md:w-40 md:h-40 rounded-full border-4 ${sportStyle.border} object-cover shadow-2xl ${sportStyle.glow}`}
                data-testid="athlete-avatar"
              />
              {athlete.verified && (
                <div className="absolute -bottom-1 -right-1 bg-[#00d4ff] rounded-full p-1.5 shadow-lg z-10">
                  <Award className="h-4 w-4 text-black" />
                </div>
              )}
              {canEdit && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  data-testid="upload-photo-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  ) : (
                    <div className="flex flex-col items-center">
                      <Camera className="h-8 w-8 text-white mb-1" />
                      <span className="text-white text-xs font-medium">Change Photo</span>
                    </div>
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="photo-file-input"
              />
            </div>

            {/* Info Block */}
            <div className="flex-1 min-w-0 text-center md:text-left">
              {/* Name + Badges */}
              <div className="flex items-center justify-center md:justify-start gap-3 flex-wrap mb-3">
                <h1 
                  className="text-2xl md:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-none"
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                  data-testid="athlete-name"
                >
                  {athlete.name}
                </h1>
                {athlete.verified && (
                  <Badge className="bg-[#00d4ff]/20 text-[#00d4ff] border-[#00d4ff]/30 text-[10px] uppercase tracking-wide font-bold">
                    <Award className="h-3 w-3 mr-1" /> Verified
                  </Badge>
                )}
                {isOwner && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] uppercase tracking-wide font-bold" data-testid="owner-badge">
                    <UserCheck className="h-3 w-3 mr-1" /> Your Profile
                  </Badge>
                )}
              </div>
              
              {/* Position & Number */}
              <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
                <span className="text-base md:text-xl text-zinc-300 font-medium">
                  {athlete.position}
                </span>
                {athlete.number > 0 && (
                  <span className={`text-2xl md:text-3xl font-black ${sportStyle.text}`} style={{ fontFamily: 'Oswald, sans-serif' }}>
                    #{athlete.number}
                  </span>
                )}
              </div>

              {/* Meta Info Row */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 md:gap-4 mb-5">
                {athlete.team && (
                  <div className="flex items-center gap-1.5 text-sm text-zinc-400">
                    <Shirt className="h-4 w-4" />
                    <span>{athlete.team}</span>
                  </div>
                )}
                <Badge className={`${sportStyle.accent}/20 ${sportStyle.text} border-current/30 text-xs uppercase tracking-wide font-bold`}>
                  {athlete.sport}
                </Badge>
                {athlete.age && (
                  <div className="flex items-center gap-1.5 text-sm text-zinc-500">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Age {athlete.age}</span>
                  </div>
                )}
              </div>

              {/* Quick Stats Row */}
              {hasStats && (
                <div className="flex items-center justify-center md:justify-start gap-6 md:gap-8 pt-4 border-t border-white/10">
                  {Object.entries(athlete.stats).slice(0, 4).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div 
                        className="text-xl md:text-2xl font-black text-white"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        {value}
                      </div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">
                        {key}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons — clear row below the hero content */}
          <div className="grid grid-cols-2 md:flex md:flex-row gap-2 mt-6 md:mt-8 md:ml-[200px]">
            {canEdit && (
              <Button 
                onClick={openEditDialog}
                className="bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wide text-xs h-10 px-5 border border-white/20" 
                data-testid="edit-profile-btn"
              >
                <Pencil className="h-4 w-4 mr-1.5" />
                Edit
              </Button>
            )}
            {canViewContact ? (
              <Button 
                className="bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold uppercase tracking-wide text-xs h-10 px-5" 
                asChild
              >
                <a href={`mailto:${athlete.contact_email || 'info@rooamsports.com'}?subject=Contact ${athlete.name}`}>
                  <Mail className="h-4 w-4 mr-1.5" />
                  Contact
                </a>
              </Button>
            ) : (
              <Button 
                className="bg-zinc-700/50 text-zinc-400 font-bold uppercase tracking-wide text-xs h-10 px-5 cursor-not-allowed"
                disabled
                data-testid="contact-locked-btn"
              >
                <Lock className="h-4 w-4 mr-1.5" />
                Contact
              </Button>
            )}
            <Button 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10 font-semibold text-xs h-10 px-5"
            >
              <Share2 className="h-4 w-4 mr-1.5" />
              Share
            </Button>
            <Button 
              className="bg-gradient-to-r from-[#ff0000] to-[#ff0000]/80 hover:from-[#ff3333] hover:to-[#ff0000] text-white font-bold uppercase tracking-wide text-xs h-10 px-5 shadow-lg shadow-red-600/20" 
              data-testid="advanced-stats-profile-btn"
              onClick={() => {
                const statsTab = document.querySelector('[value="stats"]');
                if (statsTab) statsTab.click();
                setTimeout(() => {
                  document.getElementById('stats-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              <Zap className="h-4 w-4 mr-1.5" />
              Stats
              {!canViewAdvancedStats && (
                <Crown className="h-3 w-3 ml-1.5 text-amber-300" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        {/* Claim Profile Banner - for unclaimed profiles */}
        {canClaim && (
          <div className="mb-6 bg-gradient-to-r from-[#00d4ff]/10 to-transparent border border-[#00d4ff]/30 rounded-xl p-4 md:p-5" data-testid="claim-profile-banner">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#00d4ff]/20 flex items-center justify-center shrink-0">
                <UserCheck className="w-6 h-6 text-[#00d4ff]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-1">Is this your profile?</h3>
                <p className="text-zinc-400 text-sm">
                  Claim this profile to manage your stats, connect game footage, and build your recruiting presence.
                </p>
              </div>
              <Button
                onClick={handleClaimProfile}
                disabled={claiming}
                className="bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold"
                data-testid="claim-profile-btn"
              >
                {claiming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
                Claim Profile
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="info" className="space-y-6" id="stats-section">
          <TabsList className="bg-[#141414] border border-white/10 p-1 rounded-xl">
            <TabsTrigger 
              value="info" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 text-sm font-semibold rounded-lg px-4"
            >
              Info
            </TabsTrigger>
            <TabsTrigger 
              value="roster" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 text-sm font-semibold rounded-lg px-4"
            >
              Roster ({connections.length})
            </TabsTrigger>
            <TabsTrigger 
              value="stats" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 text-sm font-semibold rounded-lg px-4"
            >
              Stats
            </TabsTrigger>
            <TabsTrigger 
              value="clips" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400 text-sm font-semibold rounded-lg px-4"
              data-testid="athlete-clips-tab"
            >
              Clips ({athleteClips.length})
            </TabsTrigger>
          </TabsList>

          {/* Info Tab */}
          <TabsContent value="info">
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="bg-[#141414] border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-sm font-semibold">Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    {[
                      ['Age', athlete.age],
                      ['Height', athlete.height],
                      ['Grade', athlete.grade],
                      ['Graduation Year', athlete.graduation_year],
                      ['School', athlete.school],
                      ['GPA', athlete.gpa],
                      ['Jersey', athlete.number ? `#${athlete.number}` : null],
                      ['Games', athlete.games],
                      ['Highlights', athlete.highlights],
                    ].filter(([, v]) => v != null).map(([label, value]) => (
                      <div key={label} className="flex justify-between border-b border-white/5 pb-2 last:border-0 last:pb-0">
                        <span className="text-zinc-500">{label}</span>
                        <span className="text-white font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              {athlete.bio && (
                <Card className="bg-[#141414] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-sm font-semibold">Bio</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-zinc-400 leading-relaxed">{athlete.bio}</p>
                  </CardContent>
                </Card>
              )}
              {/* Social Links & Contact - Only visible to college coaches, admins, or profile owner */}
              {canViewContact ? (
                (athlete.social_links && Object.values(athlete.social_links).some(v => v)) || athlete.contact_email ? (
                  <Card className="bg-[#141414] border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-sm font-semibold">Connect</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {athlete.contact_email && (
                          <a href={`mailto:${athlete.contact_email}`} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-[#00d4ff] transition-colors" data-testid="contact-email-link">
                            <Mail className="h-4 w-4" /> {athlete.contact_email}
                          </a>
                        )}
                        {athlete.social_links?.instagram && (
                          <a href={`https://instagram.com/${athlete.social_links.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-pink-400 transition-colors" data-testid="social-instagram">
                            <ExternalLink className="h-4 w-4" /> @{athlete.social_links.instagram.replace('@','')}
                          </a>
                        )}
                        {athlete.social_links?.twitter && (
                          <a href={`https://x.com/${athlete.social_links.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-blue-400 transition-colors" data-testid="social-twitter">
                            <ExternalLink className="h-4 w-4" /> @{athlete.social_links.twitter.replace('@','')}
                          </a>
                        )}
                        {athlete.social_links?.hudl && (
                          <a href={athlete.social_links.hudl.startsWith('http') ? athlete.social_links.hudl : `https://${athlete.social_links.hudl}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-orange-400 transition-colors" data-testid="social-hudl">
                            <ExternalLink className="h-4 w-4" /> Hudl Profile
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ) : null
              ) : (
                <Card className="bg-[#141414] border-white/10" data-testid="contact-gated-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-sm font-semibold flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5 text-amber-400" /> Contact Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-zinc-500 mb-3">
                      Contact information is only available to verified college coaches.
                    </p>
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-700/30 text-xs">
                      <Crown className="h-3 w-3 mr-1" /> College Coach Access
                    </Badge>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Roster Connections Tab */}
          <TabsContent value="roster">
            <Card className="bg-[#141414] border-white/10">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="text-white text-base font-semibold">Roster Connections</CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">Link your profile to teams and games you've played in</p>
                </div>
                {canEdit && (
                  <Dialog open={showConnectionDialog} onOpenChange={setShowConnectionDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold text-xs" data-testid="add-roster-connection-btn">
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#141414] border-white/10 max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-white">Link to Game Roster</DialogTitle>
                        <p className="text-xs text-zinc-500">Find the game you played in and select your team &amp; jersey number</p>
                      </DialogHeader>
                      <form onSubmit={handleAddConnection} className="space-y-4">
                        {/* Step 1: Select Game */}
                        <div>
                          <Label className="text-zinc-300 text-sm">1. Find Your Game</Label>
                          <Input
                            value={gameSearch}
                            onChange={(e) => setGameSearch(e.target.value)}
                            placeholder="Search by team name..."
                            className="bg-[#0A0A0A] border-white/10 text-white mb-2 mt-1"
                            data-testid="game-search-input"
                          />
                          <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg border border-white/10 bg-[#0A0A0A] p-1">
                            {games
                              .filter(g => {
                                if (!gameSearch) return true;
                                const q = gameSearch.toLowerCase();
                                return g.home_team_name?.toLowerCase().includes(q) || g.away_team_name?.toLowerCase().includes(q);
                              })
                              .slice(0, 20)
                              .map(g => (
                                <button
                                  key={g.game_id}
                                  type="button"
                                  onClick={() => setConnectionForm({ ...connectionForm, game_id: g.game_id, team_side: '' })}
                                  data-testid={`select-game-${g.game_id}`}
                                  className={`w-full text-left px-3 py-2 rounded text-sm transition-all ${
                                    connectionForm.game_id === g.game_id
                                      ? 'bg-[#00d4ff]/20 border border-[#00d4ff] text-white'
                                      : 'text-zinc-300 hover:bg-white/5'
                                  }`}
                                >
                                  <span className="font-medium">{g.home_team_name}</span>
                                  <span className="text-zinc-500 mx-1.5">vs</span>
                                  <span className="font-medium">{g.away_team_name}</span>
                                  <span className="text-zinc-600 text-xs ml-2 capitalize">({g.status})</span>
                                </button>
                              ))}
                            {games.length === 0 && <p className="text-xs text-zinc-600 py-2 text-center">No games found</p>}
                          </div>
                        </div>

                        {/* Step 2: Select Team Side */}
                        {connectionForm.game_id && (() => {
                          const sg = games.find(g => g.game_id === connectionForm.game_id);
                          if (!sg) return null;
                          return (
                            <div>
                              <Label className="text-zinc-300 text-sm">2. Which Team Were You On?</Label>
                              <div className="grid grid-cols-2 gap-2 mt-1">
                                <button
                                  type="button"
                                  onClick={() => setConnectionForm({ ...connectionForm, team_side: 'home' })}
                                  data-testid="select-home-team"
                                  className={`p-3 rounded-lg border text-center transition-all ${
                                    connectionForm.team_side === 'home'
                                      ? 'bg-blue-600/20 border-blue-500 text-white'
                                      : 'border-zinc-700 text-gray-400 hover:border-zinc-500'
                                  }`}
                                >
                                  <p className="font-semibold text-sm">{sg.home_team_name}</p>
                                  <p className="text-[10px] text-gray-500">Home</p>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConnectionForm({ ...connectionForm, team_side: 'away' })}
                                  data-testid="select-away-team"
                                  className={`p-3 rounded-lg border text-center transition-all ${
                                    connectionForm.team_side === 'away'
                                      ? 'bg-red-600/20 border-red-500 text-white'
                                      : 'border-zinc-700 text-gray-400 hover:border-zinc-500'
                                  }`}
                                >
                                  <p className="font-semibold text-sm">{sg.away_team_name}</p>
                                  <p className="text-[10px] text-gray-500">Away</p>
                                </button>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Step 3: Jersey Number */}
                        {connectionForm.team_side && (
                          <div>
                            <Label className="text-zinc-300 text-sm">3. Your Jersey Number</Label>
                            <Input
                              type="number"
                              value={connectionForm.jersey_number}
                              onChange={(e) => setConnectionForm({...connectionForm, jersey_number: e.target.value})}
                              placeholder="Jersey # you wore in this game"
                              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                              required
                              data-testid="connection-jersey-input"
                            />
                          </div>
                        )}

                        {/* Optional notes */}
                        {connectionForm.jersey_number && (
                          <div>
                            <Label className="text-zinc-300 text-sm">Notes (optional)</Label>
                            <Input
                              value={connectionForm.notes}
                              onChange={(e) => setConnectionForm({...connectionForm, notes: e.target.value})}
                              placeholder="e.g. Guest player, borrowed jersey"
                              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                            />
                          </div>
                        )}

                        <Button
                          type="submit"
                          disabled={saving || !connectionForm.game_id || !connectionForm.team_side || !connectionForm.jersey_number}
                          className="w-full bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold disabled:opacity-40"
                          data-testid="submit-connection-btn"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-2" />Link to Game Roster</>}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {connections.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No roster connections yet</p>
                    <p className="text-gray-600 text-xs mt-1">Connect your profile to teams you've played for</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {connections.map((conn) => (
                      <div key={conn.id} className="flex items-center justify-between bg-rooam-black rounded-lg px-4 py-3" data-testid={`connection-${conn.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-rooam-blue/20 flex items-center justify-center shrink-0">
                            <Hash className="h-5 w-5 text-rooam-blue" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-white font-semibold text-sm">{conn.team_name}</span>
                              <Badge className="bg-rooam-blue/20 text-rooam-blue border-rooam-blue text-[10px]">
                                #{conn.jersey_number}
                              </Badge>
                            </div>
                            {conn.game_name && (
                              <p className="text-xs text-gray-500 truncate">{conn.game_name}</p>
                            )}
                            {conn.notes && (
                              <p className="text-[10px] text-gray-600 italic">{conn.notes}</p>
                            )}
                          </div>
                        </div>
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0 shrink-0" onClick={() => handleRemoveConnection(conn.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {!isAuthenticated() && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500">
                      <Link to="/login" className="text-rooam-blue hover:underline">Sign in</Link> to add roster connections
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="space-y-6">
              {/* Basic Stats Card */}
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-[#00d4ff]" />
                    Basic Stats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {athlete.stats && Object.keys(athlete.stats).length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {Object.entries(athlete.stats).map(([key, value]) => (
                        <div key={key} className="text-center bg-[#0A0A0A] border border-white/5 rounded-xl p-4">
                          <div 
                            className="text-2xl font-black text-white"
                            style={{ fontFamily: 'Oswald, sans-serif' }}
                          >
                            {value}
                          </div>
                          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold mt-1">{key}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <TrendingUp className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
                      <p className="text-zinc-400 text-sm">No basic stats recorded yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Advanced Stats Section - Elite Gated */}
              <Card className="bg-gradient-to-r from-[#ff0000]/10 via-[#141414] to-[#141414] border-[#ff0000]/20">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Zap className="h-4 w-4 text-[#ff0000]" />
                      Advanced Stats
                      {!canViewAdvancedStats && (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] ml-2">
                          <Crown className="h-3 w-3 mr-1" />
                          Elite
                        </Badge>
                      )}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {!canViewAdvancedStats ? (
                    // Elite Upgrade Prompt
                    <div className="text-center py-8">
                      <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                        <Crown className="h-7 w-7 text-amber-400" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">Elite Access Required</h3>
                      <p className="text-zinc-400 text-sm mb-6 max-w-md mx-auto">
                        Upgrade to <span className="text-amber-400 font-semibold">Rooam Elite</span> to unlock 
                        AI-powered stats, shooting splits, achievements, and highlight reels.
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 max-w-md mx-auto">
                        {[
                          { icon: BarChart3, label: 'Shooting Splits' },
                          { icon: Trophy, label: 'Achievements' },
                          { icon: Sparkles, label: 'Advanced Stats' },
                          { icon: Video, label: 'Highlights' },
                        ].map(({ icon: Icon, label }) => (
                          <div key={label} className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                            <Lock className="h-4 w-4 text-zinc-600 mx-auto mb-1" />
                            <span className="text-[10px] text-zinc-500">{label}</span>
                          </div>
                        ))}
                      </div>
                      <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold" asChild>
                        <Link to="/membership">
                          <Crown className="h-4 w-4 mr-2" />
                          Upgrade to Elite
                        </Link>
                      </Button>
                    </div>
                  ) : loadingStats ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 text-[#ff0000] animate-spin" />
                    </div>
                  ) : !sportsvisioStatus?.linked ? (
                    // Not linked to SportsVisio
                    <div className="text-center py-8">
                      <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                        <BarChart3 className="h-7 w-7 text-zinc-600" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">Stats Not Available Yet</h3>
                      <p className="text-gray-500 text-sm max-w-sm mx-auto">
                        Advanced stats will appear here once games are recorded and processed by our AI system.
                      </p>
                    </div>
                  ) : (
                    // Full Advanced Stats View
                    <AdvancedStatsContent stats={advancedStats} athlete={athlete} />
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clips Tab */}
          <TabsContent value="clips">
            <Card className="bg-[#141414] border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Scissors className="h-4 w-4 text-red-500" />
                  Highlight Clips
                </CardTitle>
                <p className="text-xs text-zinc-500 mt-1">Clips where {athlete.name} has been tagged</p>
              </CardHeader>
              <CardContent>
                {loadingClips ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 text-red-500 animate-spin" />
                  </div>
                ) : athleteClips.length === 0 ? (
                  <div className="text-center py-8">
                    <Video className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400 text-sm">No highlight clips yet</p>
                    <p className="text-zinc-600 text-xs mt-1">Clips will appear here when {athlete.name} is tagged in game highlights</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {athleteClips.map((clip) => (
                      <Link
                        key={clip.clip_id}
                        to={`/clip/${clip.clip_id}`}
                        className="flex items-center justify-between bg-[#0A0A0A] hover:bg-white/5 rounded-lg px-4 py-3 transition-colors group"
                        data-testid={`athlete-clip-${clip.clip_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center shrink-0">
                            <Play className="h-5 w-5 text-red-500" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-sm font-semibold truncate group-hover:text-red-400 transition-colors">{clip.title}</p>
                            <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-0.5">
                              <span>{clip.home_team} vs {clip.away_team}</span>
                              <span className="text-zinc-700">|</span>
                              <span className="font-mono">{clip.duration}s</span>
                              {clip.views > 0 && (
                                <>
                                  <span className="text-zinc-700">|</span>
                                  <span><Eye className="w-3 h-3 inline mr-0.5" />{clip.views}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 text-zinc-500">
                          {clip.likes > 0 && (
                            <span className="flex items-center gap-1 text-xs">
                              <Heart className="w-3 h-3 text-red-400 fill-red-400" />{clip.likes}
                            </span>
                          )}
                          <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Profile Dialog */}
        {showEditDialog && (
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="bg-[#141414] border-white/10 max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="edit-profile-dialog">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Pencil className="h-4 w-4 text-[#00d4ff]" />
                  Edit Profile
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Bio</Label>
                  <textarea
                    value={editForm.bio}
                    onChange={e => setEditForm({...editForm, bio: e.target.value})}
                    placeholder="Tell coaches and scouts about yourself..."
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#00d4ff] mt-1 min-h-[80px] resize-y"
                    maxLength={500}
                    data-testid="edit-bio-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">Position</Label>
                    <Input value={editForm.position} onChange={e => setEditForm({...editForm, position: e.target.value})} placeholder="e.g. Point Guard" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-position-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-sm">Jersey Number</Label>
                    <Input type="number" value={editForm.number} onChange={e => setEditForm({...editForm, number: e.target.value})} placeholder="#" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-number-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">Height</Label>
                    <Input value={editForm.height} onChange={e => setEditForm({...editForm, height: e.target.value})} placeholder={`e.g. 6'2"`} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-height-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-sm">Date of Birth</Label>
                    <Input type="date" value={editForm.date_of_birth ? editForm.date_of_birth.slice(0,10) : ''} onChange={e => setEditForm({...editForm, date_of_birth: e.target.value})} className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-dob-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">Grade/Class</Label>
                    <Input value={editForm.grade} onChange={e => setEditForm({...editForm, grade: e.target.value})} placeholder="e.g. Junior" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-grade-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-sm">Graduation Year</Label>
                    <Input value={editForm.graduation_year} onChange={e => setEditForm({...editForm, graduation_year: e.target.value})} placeholder="e.g. 2027" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-gradyear-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">School</Label>
                    <Input value={editForm.school} onChange={e => setEditForm({...editForm, school: e.target.value})} placeholder="School name" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-school-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-sm">Team</Label>
                    <Input value={editForm.team} onChange={e => setEditForm({...editForm, team: e.target.value})} placeholder="Team name" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-team-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Contact Email</Label>
                  <Input type="email" value={editForm.contact_email} onChange={e => setEditForm({...editForm, contact_email: e.target.value})} placeholder="your.email@school.edu" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-email-input" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">GPA</Label>
                  <Input value={editForm.gpa} onChange={e => setEditForm({...editForm, gpa: e.target.value})} placeholder="e.g. 3.8" className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="edit-gpa-input" />
                </div>
                
                {/* Social Links */}
                <div>
                  <Label className="text-zinc-300 text-sm mb-2 block">Social Links</Label>
                  <div className="space-y-2">
                    <Input 
                      value={editForm.social_links?.instagram || ''} 
                      onChange={e => setEditForm({...editForm, social_links: {...(editForm.social_links || {}), instagram: e.target.value}})} 
                      placeholder="Instagram handle" 
                      className="bg-[#0A0A0A] border-white/10 text-white" 
                      data-testid="edit-instagram-input" 
                    />
                    <Input 
                      value={editForm.social_links?.twitter || ''} 
                      onChange={e => setEditForm({...editForm, social_links: {...(editForm.social_links || {}), twitter: e.target.value}})} 
                      placeholder="X / Twitter handle" 
                      className="bg-[#0A0A0A] border-white/10 text-white" 
                      data-testid="edit-twitter-input" 
                    />
                    <Input 
                      value={editForm.social_links?.hudl || ''} 
                      onChange={e => setEditForm({...editForm, social_links: {...(editForm.social_links || {}), hudl: e.target.value}})} 
                      placeholder="Hudl profile URL" 
                      className="bg-[#0A0A0A] border-white/10 text-white" 
                      data-testid="edit-hudl-input" 
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="w-full bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

// Advanced Stats Content Component
const AdvancedStatsContent = ({ stats, athlete }) => {
  const displayStats = stats?.aggregated || athlete?.stats || {};
  const trophies = stats?.trophies || {};
  
  // Trophy config
  const TROPHY_CONFIG = {
    potg: { label: 'Player of the Game', icon: Trophy, color: 'text-amber-400 bg-amber-500/20 border-amber-500/30' },
    sniper: { label: 'Sniper', icon: Target, color: 'text-red-400 bg-red-500/20 border-red-500/30' },
    thief: { label: 'Thief', icon: Zap, color: 'text-purple-400 bg-purple-500/20 border-purple-500/30' },
    dimer: { label: 'Dimer', icon: Sparkles, color: 'text-green-400 bg-green-500/20 border-green-500/30' },
  };

  const games = displayStats.totalGames || athlete?.games || 1;

  return (
    <div className="space-y-6">
      {/* Trophies */}
      {Object.keys(trophies).length > 0 && (
        <div>
          <h4 className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3">Achievements</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(trophies).map(([key, count]) => {
              if (!count) return null;
              const config = TROPHY_CONFIG[key];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <Badge key={key} className={`${config.color} border text-xs px-2.5 py-1 flex items-center gap-1.5`}>
                  <Icon className="h-3.5 w-3.5" />
                  {config.label}
                  {count > 1 && <span className="opacity-70">×{count}</span>}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* Shooting Splits */}
      <div>
        <h4 className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3">Shooting Splits</h4>
        <div className="grid grid-cols-3 gap-3">
          {['FG', '3PT', 'FT'].map((label) => (
            <div key={label} className="bg-rooam-black border border-white/10 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">
                {displayStats[`${label.toLowerCase()}Pct`] || '-'}%
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mt-1">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-Game Averages */}
      <div>
        <h4 className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3">Per-Game Averages</h4>
        <div className="grid grid-cols-5 gap-2">
          {[
            { key: 'PPG', value: displayStats.PPG || Math.round((displayStats.totalPoints || 0) / games * 10) / 10 },
            { key: 'RPG', value: displayStats.RPG || Math.round((displayStats.totalRebounds || 0) / games * 10) / 10 },
            { key: 'APG', value: displayStats.APG || Math.round((displayStats.assists || 0) / games * 10) / 10 },
            { key: 'SPG', value: displayStats.SPG || Math.round((displayStats.steals || 0) / games * 10) / 10 },
            { key: 'BPG', value: displayStats.BPG || Math.round((displayStats.blocks || 0) / games * 10) / 10 },
          ].map(({ key, value }) => (
            <div key={key} className="bg-rooam-black border border-white/10 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-[#00d4ff]">{value || '-'}</div>
              <div className="text-[9px] text-zinc-600 uppercase tracking-wider font-bold">{key}</div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Summary */}
      {stats?.ai_summary && (
        <div>
          <h4 className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-[#ff0000]" />
            AI Performance Summary
          </h4>
          <div className="bg-gradient-to-r from-[#ff0000]/10 to-transparent border border-[#ff0000]/20 rounded-xl p-4">
            <p className="text-sm text-zinc-300 leading-relaxed">{stats.ai_summary}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AthleteProfile;
